import React, { useRef, useState } from 'react';
import { format } from 'date-fns';
import { TaskAttachment } from '../../types/task.types';
import taskService from '../../services/task.service';
import { LedgerDialog } from '../ui';
import { cn } from '../../lib/cn';

interface TaskAttachmentListProps {
  taskId: string;
  attachments: TaskAttachment[];
  canDelete?: boolean;
  onAttachmentDeleted?: () => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const fileExtensionLabel = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toUpperCase();
  return extension || 'FILE';
};

const TaskAttachmentList: React.FC<TaskAttachmentListProps> = ({
  taskId,
  attachments,
  canDelete = false,
  onAttachmentDeleted,
}) => {
  const [downloadingAttachments, setDownloadingAttachments] = useState<Set<string>>(new Set());
  const [deletingAttachments, setDeletingAttachments] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<TaskAttachment | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleDownload = async (attachment: TaskAttachment) => {
    try {
      setDownloadingAttachments((prev) => new Set(prev).add(attachment.id));
      await taskService.downloadTaskAttachment(taskId, attachment.id, attachment.file_name);
    } catch (error) {
      console.error('Failed to download attachment:', error);
    } finally {
      setDownloadingAttachments((prev) => {
        const newSet = new Set(prev);
        newSet.delete(attachment.id);
        return newSet;
      });
    }
  };

  const handleDeleteClick = (attachment: TaskAttachment) => {
    setAttachmentToDelete(attachment);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!attachmentToDelete) return;

    try {
      setDeletingAttachments((prev) => new Set(prev).add(attachmentToDelete.id));
      setDeleteDialogOpen(false);

      await taskService.deleteTaskAttachment(taskId, attachmentToDelete.id);

      if (onAttachmentDeleted) {
        onAttachmentDeleted();
      }
    } catch (error) {
      console.error('Failed to delete attachment:', error);
    } finally {
      setDeletingAttachments((prev) => {
        const newSet = new Set(prev);
        newSet.delete(attachmentToDelete.id);
        return newSet;
      });
      setAttachmentToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAttachmentToDelete(null);
  };

  if (attachments.length === 0) {
    return <p className="py-6 text-center font-serif text-scale-1 italic text-ink-muted">No attachments</p>;
  }

  return (
    <>
      <div>
        {attachments.map((attachment) => {
          const isDownloading = downloadingAttachments.has(attachment.id);
          const isDeleting = deletingAttachments.has(attachment.id);
          return (
            <div
              key={attachment.id}
              data-testid="attachment-row"
              className={cn(
                'flex items-center justify-between gap-3 border-b border-rule-faint py-2 transition-opacity',
                isDeleting && 'opacity-50',
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="cap-label shrink-0 border border-ink px-1">
                    {fileExtensionLabel(attachment.file_name)}
                  </span>
                  <span className="truncate text-scale-1 font-medium text-ink" title={attachment.file_name}>
                    {attachment.file_name}
                  </span>
                  <span className="shrink-0 font-mono text-scale-0 text-ink-muted">
                    {formatFileSize(attachment.file_size)}
                  </span>
                </div>
                <div className="mt-0.5 text-scale-0 text-ink-muted">
                  Uploaded by {attachment.uploaded_by_name || attachment.uploaded_by_email || 'Unknown'} &middot;{' '}
                  {format(new Date(attachment.uploaded_at), 'MMM d, yyyy HH:mm')}
                </div>
              </div>

              <div className="flex shrink-0 items-baseline gap-3">
                <button
                  type="button"
                  onClick={() => handleDownload(attachment)}
                  disabled={isDownloading}
                  className="cap-label cursor-pointer hover:!text-ink disabled:cursor-default disabled:opacity-60"
                >
                  {isDownloading ? 'Downloading…' : 'Download'}
                </button>

                {canDelete && (
                  <button
                    type="button"
                    aria-label={`Delete ${attachment.file_name}`}
                    onClick={() => handleDeleteClick(attachment)}
                    disabled={isDeleting}
                    className="cap-label cursor-pointer text-sev-critical hover:opacity-70 disabled:cursor-default disabled:opacity-40"
                  >
                    {isDeleting ? 'Deleting…' : 'Delete'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <LedgerDialog
        open={deleteDialogOpen}
        onOpenChange={(next) => {
          if (!next) handleDeleteCancel();
        }}
        title="Delete attachment?"
        description={
          attachmentToDelete
            ? `Are you sure you want to delete "${attachmentToDelete.file_name}"? This action cannot be undone.`
            : undefined
        }
        size="sm"
        initialFocus={cancelRef}
        footer={
          <>
            <button
              type="button"
              ref={cancelRef}
              onClick={handleDeleteCancel}
              className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteConfirm}
              className="cursor-pointer bg-sev-critical px-3 py-1.5 text-scale-1 font-semibold text-paper hover:opacity-90"
            >
              Delete
            </button>
          </>
        }
      >
        {null}
      </LedgerDialog>
    </>
  );
};

export default TaskAttachmentList;
